import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import { fieldLabelSx, inputWrapperSx } from '../common/formFieldStyles';
import { getServiceName } from '../../utils/serviceTranslations';
import {
  FRESHA_APPOINTMENT_STATUS_OPTIONS,
  WALK_IN_CUSTOMER_OPTION_ID,
  parseFreshaAppointmentStatus,
  revalidateAppointmentRow,
} from '../../utils/freshaAppointmentCsv';
import { BRAND } from '../../theme/brandTokens';

const emptyDraft = {
  status: 'Novo',
  customerOption: null,
  serviceOption: null,
  teamMemberOption: null,
};

const readOnlyInputStyle = {
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  backgroundColor: '#f5f5f5',
  color: '#555',
  boxSizing: 'border-box',
  cursor: 'default',
};

const autocompleteSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    '& fieldset': { borderWidth: 2, borderColor: BRAND.line },
    '&:hover fieldset': { borderColor: BRAND.accent },
    '&.Mui-focused fieldset': { borderColor: BRAND.accent },
  },
};

const formatPriceDisplay = (unitPrice) => {
  if (unitPrice == null || unitPrice === '') {
    return '—';
  }
  return String(unitPrice);
};

const FixFailedImportAppointmentsDialog = ({
  open,
  onClose,
  rows,
  onApply,
  catalogs,
}) => {
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [draft, setDraft] = useState(emptyDraft);

  const failingRows = useMemo(
    () => rows.filter((row) => !row.skipped && row.errors.length > 0),
    [rows],
  );

  const customerOptions = useMemo(() => {
    const walkIn = {
      id: WALK_IN_CUSTOMER_OPTION_ID,
      label: t('dataImport.appointments.resolve.walkIn'),
      isWalkIn: true,
    };
    const customers = (catalogs?.customers || []).map((customer) => ({
      id: customer.customerId,
      label: customer.email ? `${customer.name} (${customer.email})` : customer.name,
      name: customer.name,
      isWalkIn: false,
    }));
    return [walkIn, ...customers];
  }, [catalogs?.customers, t]);

  const serviceOptions = useMemo(() => (
    (catalogs?.services || []).map((service) => ({
      id: service.id,
      label: getServiceName(service.name, i18n.language, catalogs?.language),
      service,
    }))
  ), [catalogs?.services, catalogs?.language, i18n.language]);

  const teamMemberOptions = useMemo(() => (
    (catalogs?.teamMembers || []).map((member) => ({
      id: member.id,
      label: member.name,
      member,
    }))
  ), [catalogs?.teamMembers]);

  const buildDraftFromRow = (row) => {
    let customerOption = null;
    if (row.useWalkInCustomer) {
      customerOption = customerOptions.find((option) => option.isWalkIn) || null;
    } else if (row.resolvedCustomerId) {
      customerOption = customerOptions.find((option) => option.id === row.resolvedCustomerId) || null;
    }

    return {
      status: row.status || 'Novo',
      customerOption,
      serviceOption: serviceOptions.find((option) => option.id === row.resolvedServiceId) || null,
      teamMemberOption: teamMemberOptions.find((option) => option.id === row.resolvedTeamMemberId) || null,
    };
  };

  useEffect(() => {
    if (!open) {
      setCurrentIndex(0);
      setDraft(emptyDraft);
      return;
    }

    if (failingRows.length === 0) {
      setCurrentIndex(0);
      setDraft(emptyDraft);
      return;
    }

    setCurrentIndex((index) => Math.min(index, failingRows.length - 1));
  }, [open, failingRows.length]);

  const selectedRow = failingRows[currentIndex] || null;

  useEffect(() => {
    if (!open || !selectedRow) {
      return;
    }

    setDraft(buildDraftFromRow(selectedRow));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild draft when selected failing row changes
  }, [open, selectedRow?.rowNumber, customerOptions, serviceOptions, teamMemberOptions]);

  const isDraftValid = useMemo(() => {
    if (!selectedRow) {
      return false;
    }

    if (!draft.customerOption || !draft.serviceOption || !draft.teamMemberOption) {
      return false;
    }

    return parseFreshaAppointmentStatus(draft.status).known;
  }, [draft, selectedRow]);

  const handleNext = () => {
    if (!selectedRow || !isDraftValid) {
      return;
    }

    const useWalkInCustomer = Boolean(draft.customerOption?.isWalkIn);
    const nextRow = revalidateAppointmentRow({
      ...selectedRow,
      customerName: selectedRow.customerName || draft.customerOption?.name || '',
      teamMemberName: selectedRow.teamMemberName || draft.teamMemberOption?.label || '',
      serviceName: selectedRow.serviceName || draft.serviceOption?.label || '',
      categoryName: selectedRow.categoryName || '',
      status: draft.status,
      scheduledDateRaw: selectedRow.scheduledDateRaw || selectedRow.scheduledDate || '',
      periodFrom: selectedRow.periodFrom || '',
      periodTo: selectedRow.periodTo || '',
      unitPrice: selectedRow.unitPrice,
      useWalkInCustomer,
      resolvedCustomerId: useWalkInCustomer ? null : (draft.customerOption?.id || null),
      resolvedServiceId: draft.serviceOption?.id || null,
      resolvedTeamMemberId: draft.teamMemberOption?.id || null,
      skipped: false,
    }, catalogs);

    if (nextRow.errors.length > 0) {
      return;
    }

    onApply(nextRow);
  };

  const handleSkip = () => {
    if (!selectedRow) {
      return;
    }

    onApply({
      ...selectedRow,
      skipped: true,
      errors: [],
    });
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseButton
      dividers
      title={t('dataImport.appointments.fixDialog.title')}
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} color="inherit">{t('common.cancel')}</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleSkip} color="inherit" disabled={!selectedRow}>
              {t('dataImport.appointments.resolve.skipRow')}
            </Button>
            <PrimaryActionButton onClick={handleNext} disabled={!isDraftValid}>
              {t('dataImport.appointments.fixDialog.next')}
            </PrimaryActionButton>
          </Box>
        </>
      )}
    >
      {failingRows.length === 0 ? (
        <Typography>{t('dataImport.appointments.fixDialog.noFailures')}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Alert severity="info">
            {t('dataImport.appointments.fixDialog.progress', {
              current: currentIndex + 1,
              total: failingRows.length,
              row: selectedRow.rowNumber,
              name: selectedRow.freshaAppointmentReference || '—',
            })}
          </Alert>

          <Alert severity="info">
            {t('dataImport.appointments.resolve.importedValues', {
              customer: selectedRow.customerName || '—',
              service: selectedRow.serviceName || '—',
              teamMember: selectedRow.teamMemberName || '—',
            })}
          </Alert>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={fieldLabelSx}>{t('dataImport.appointments.fields.customer')} *</Typography>
              <Autocomplete
                options={customerOptions}
                value={draft.customerOption}
                onChange={(_, value) => setDraft((prev) => ({ ...prev, customerOption: value }))}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('dataImport.appointments.resolve.searchCustomer')}
                    error={!draft.customerOption}
                  />
                )}
                sx={autocompleteSx}
              />
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={fieldLabelSx}>{t('dataImport.appointments.fields.service')} *</Typography>
              <Autocomplete
                options={serviceOptions}
                value={draft.serviceOption}
                onChange={(_, value) => setDraft((prev) => ({ ...prev, serviceOption: value }))}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('dataImport.appointments.resolve.searchService')}
                    error={!draft.serviceOption}
                  />
                )}
                sx={autocompleteSx}
              />
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={fieldLabelSx}>{t('dataImport.appointments.fields.teamMember')} *</Typography>
              <Autocomplete
                options={teamMemberOptions}
                value={draft.teamMemberOption}
                onChange={(_, value) => setDraft((prev) => ({ ...prev, teamMemberOption: value }))}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('dataImport.appointments.resolve.searchTeamMember')}
                    error={!draft.teamMemberOption}
                  />
                )}
                sx={autocompleteSx}
              />
            </Box>

            <Box>
              <SalovareSelectField
                label={`${t('dataImport.appointments.fields.status')} *`}
                value={draft.status}
                onChange={(event) => setDraft((prev) => ({ ...prev, status: event.target.value }))}
              >
                {FRESHA_APPOINTMENT_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuItem>
                ))}
              </SalovareSelectField>
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.appointments.fields.scheduledDate')}</Typography>
              <Box
                component="input"
                readOnly
                value={selectedRow.scheduledDateRaw || selectedRow.scheduledDate || '—'}
                sx={{ ...readOnlyInputStyle, ...inputWrapperSx }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.appointments.fields.periodFrom')}</Typography>
              <Box
                component="input"
                readOnly
                value={selectedRow.periodFrom || '—'}
                sx={{ ...readOnlyInputStyle, ...inputWrapperSx }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.appointments.fields.periodTo')}</Typography>
              <Box
                component="input"
                readOnly
                value={selectedRow.periodTo || '—'}
                sx={{ ...readOnlyInputStyle, ...inputWrapperSx }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.appointments.fields.price')}</Typography>
              <Box
                component="input"
                readOnly
                value={formatPriceDisplay(selectedRow.unitPrice)}
                sx={{ ...readOnlyInputStyle, ...inputWrapperSx }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </ScrollableDialog>
  );
};

export default FixFailedImportAppointmentsDialog;
